import { type Car, type Brand } from '@prisma/client'
import { useEffect, useState } from 'react'
import { cn, getImgSrc } from '#app/shared/lib/misc.js'
import {
  calculateDiscountedPrice,
  calculateFinancedAmount,
  formatDiscountedPrice,
  formatPrice,
  getApplicableTIN,
} from '#app/shared/lib/price.ts'
import CarSpecialFeatures from '#app/shared/ui/data-display/carDescription/CarSpecialFeatures.js'
import { PackOptimaWithModal } from '#app/shared/ui/data-display/PackOptimaComponents.tsx'
import { Separator } from '#app/shared/ui/data-display/Separator.tsx'
import {
  EcoLabelEnum,
  FuelTypeEnum,
  TransmissionEnum,
  type WarrantyDurationTypes,
  type WarrantyTypes,
  type TypeOfPaymentType,
  WarrantyTranslationEnum,
} from '../../../../../types/enums'
import { warrantyPriceByWarrantyType } from '../../reservar/ui/warranty'
import VehicleCertificates from './vehicleCertificates'

type Props = {
  className?: string
  car: Car & { brand: Brand; images: { id: string }[] }
  paymentData?: {
    typeOfPayment: TypeOfPaymentType | undefined
    warrantyChoice: {
      type: WarrantyTypes
      duration: WarrantyDurationTypes
    }
    activeNumber: number
    initialEntry: number
    hasPackOptima: boolean
    maxPackGamaEntry: number
  }
}

export default function CarSummary({ className, car, paymentData }: Props) {
  const etiquetaImages: Record<
    (typeof EcoLabelEnum)[keyof typeof EcoLabelEnum],
    string
  > = {
    [EcoLabelEnum.ECO]: '/img/Etiquetas_Conbustible/Etiqueta_ECO.png',
    [EcoLabelEnum.C]: '/img/Etiquetas_Conbustible/Etiqueta_C.png',
    [EcoLabelEnum.CERO]: '/img/Etiquetas_Conbustible/Etiqueta_0.png',
    [EcoLabelEnum.B]: '/img/Etiquetas_Conbustible/Etiqueta_B.png',
  }

  const validEcoLabel = Object.values(EcoLabelEnum).includes(
    car.ecoLabel as (typeof EcoLabelEnum)[keyof typeof EcoLabelEnum],
  )
    ? car.ecoLabel
    : EcoLabelEnum.B

  const features = [
    `${car.matriculation ? car.matriculation.getFullYear() : car.year}`,
    `${car.mileage.toLocaleString('es-ES')} km`,
    `${car.horsePower} cv`,
    Object.values(TransmissionEnum).includes(
      car.transmission as (typeof TransmissionEnum)[keyof typeof TransmissionEnum],
    )
      ? car.transmission
      : 'N/A',
    Object.values(FuelTypeEnum).includes(
      car.fuelType as (typeof FuelTypeEnum)[keyof typeof FuelTypeEnum],
    )
      ? car.fuelType
      : 'N/A',
  ]

  const inCashPrice = formatDiscountedPrice(car.price, car.discount)

  const [leftPrice, setLeftPrice] = useState<{
    label: string
    price: string | React.ReactNode
  }>({
    label: 'PRECIO AL CONTADO',
    price: inCashPrice,
  })

  const financedPrice = calculateDiscountedPrice(
    car.minFinancedAmount ?? 0,
    car.discount,
  )

  const [rightPrice, setRightPrice] = useState<{
    label: string
    price: string | React.ReactNode
  }>({
    label: 'PRECIO FINANCIADO',
    price: formatPrice(Math.round(financedPrice ?? 0)),
  })
  const [fianceAmounts, setFianceAmounts] = useState<{
    capitalToFinance: number
    totalFinanceDebt: number
    applicableTIN: number
  }>(() => {
    if (paymentData?.typeOfPayment === 'Financiado') {
      const applicableTIN = getApplicableTIN(
        paymentData.hasPackOptima,
        car.annualInterestRate ?? 0,
        car.annualInterestRatePackGo,
      )

      const { quota, total, capitalToFinance } = calculateFinancedAmount({
        price: financedPrice ?? 0,
        warrantyData: paymentData.warrantyChoice,
        initialEntry: paymentData.initialEntry,
        months: paymentData.activeNumber,
        TIN: applicableTIN,
        hasPackOptima: paymentData.hasPackOptima,
      })

      return {
        capitalToFinance,
        totalFinanceDebt: total,
        monthlyPayment: quota || 0,
        applicableTIN,
      }
    }
    return {
      capitalToFinance: 0,
      totalFinanceDebt: 0,
      monthlyPayment: 0,
      applicableTIN: 0,
    }
  })

  useEffect(() => {
    if (!paymentData) return
    if (paymentData.typeOfPayment === 'Financiado') {
      const applicableTIN = getApplicableTIN(
        paymentData.hasPackOptima,
        car.annualInterestRate ?? 0,
        car.annualInterestRatePackGo,
      )
      const { capitalToFinance, quota, total } = calculateFinancedAmount({
        price: financedPrice ?? 0,
        warrantyData: paymentData.warrantyChoice,
        initialEntry: paymentData.initialEntry,
        months: paymentData.activeNumber,
        TIN: applicableTIN,
        hasPackOptima: paymentData.hasPackOptima,
      })

      setFianceAmounts({
        capitalToFinance,
        totalFinanceDebt: total,
        applicableTIN,
      })

      setLeftPrice({
        label: 'TU CUOTA',
        price: (
          <>
            <span className="text-primary">{Math.round(quota)}</span>
            <span className="text-base text-neutral-500 font-normal">
              {' '}
              /mes
            </span>
          </>
        ),
      })
    }

    if (paymentData.typeOfPayment === 'Al contado') {
      setLeftPrice({
        label: 'PRECIO TOTAL',
        price: formatDiscountedPrice(
          car.price,
          car.discount,
          warrantyPriceByWarrantyType({
            ...paymentData.warrantyChoice,
            hasPackOptima: paymentData.hasPackOptima,
          }),
        ),
      })
    }

    if (paymentData.typeOfPayment === 'Al contado') {
      setRightPrice({
        label: 'PRECIO AL CONTADO',
        price: inCashPrice,
      })
    }
  }, [paymentData])

  const machanicAndBatteryWarrantyDetails = [
    ...(car.manufacturerBatteryWarranty &&
    car.manufacturerBatteryWarrantyExpiration
      ? [
          {
            label: 'Garantía Oficial de Batería',
            value: `hasta el ${car?.manufacturerBatteryWarrantyExpiration?.toLocaleDateString()}`,
          },
        ]
      : []),
    ...(car.manufacturerMechanicalWarranty &&
    car.manufacturerMechanicalWarrantyExpiration
      ? [
          {
            label: 'Garantía Oficial Mecánica',
            value: `hasta el ${car?.manufacturerMechanicalWarrantyExpiration?.toLocaleDateString()}`,
          },
        ]
      : []),
  ]

  return (
    <div
      className={cn(
        `
          w-full overflow-clip

          xl:w-full xl:min-w-[380px] xl:max-w-[428px]
        `,
        className,
      )}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        <img
          src={getImgSrc(car.images[0]?.id, 'carImage')}
          alt="car"
          className="h-full w-full object-cover"
        />
      </div>
      <div
        className={`
          relative w-full border border-b-0 border-neutral-75 bg-white pt-11

          xl:w-[428px]
        `}
      >
        <img
          className="absolute right-5 top-5 h-5 w-5"
          src={
            etiquetaImages[
              validEcoLabel as (typeof EcoLabelEnum)[keyof typeof EcoLabelEnum]
            ]
          }
          alt="eco label"
        />
        <div className={`flex flex-col gap-4 px-6`}>
          <p className="text-h4 text-neutral-900">
            {car.brand.name} {car.model}
          </p>

          <p className="text-base-medium text-neutral-700">{car.description}</p>

          <CarSpecialFeatures features={features} />
          <Separator className="w-full bg-neutral-100" />
        </div>
        <div className="flex items-start justify-between px-6">
          {/* Left Section */}
          <div className="mt-3 flex min-w-0 flex-1 flex-col">
            <div className="flex flex-col">
              <p
                className={`
                  whitespace-nowrap text-subtitle2-semibold text-neutral-500
                `}
              >
                {leftPrice.label}
              </p>
              <p
                className={`
                  overflow-hidden whitespace-nowrap text-h4 text-neutral-900

                  xl:text-h3
                `}
              >
                {leftPrice.price}
              </p>
            </div>

            {paymentData?.typeOfPayment === 'Financiado' &&
              paymentData.hasPackOptima && (
                <div className="mt-1">
                  <PackOptimaWithModal size="small" />
                </div>
              )}
          </div>

          <Separator
            orientation="vertical"
            className={`
              h-[85px] bg-neutral-100

              ${
                paymentData?.typeOfPayment === 'Financiado' &&
                paymentData.hasPackOptima &&
                'h-24'
              }
            `}
          />

          {/* Right Section */}
          <div className="relative mt-3 flex min-w-0 flex-1 flex-col pl-5">
            <p
              className={`
                whitespace-nowrap text-subtitle2-semibold text-neutral-500
              `}
            >
              {rightPrice.label}
            </p>
            <p
              className={`
                overflow-hidden whitespace-nowrap text-h4 text-neutral-900

                xl:text-h3
              `}
            >
              {rightPrice.price}
            </p>
            {paymentData?.typeOfPayment !== 'Al contado' && (
              <span
                className={`absolute left-5 top-full text-xs text-neutral-400`}
              >
                sólo particulares y autónomos
              </span>
            )}
          </div>
        </div>

        {paymentData?.typeOfPayment && (
          <div
            className={`
              flex flex-col gap-2 border-y border-neutral-75 p-6
              text-subtitle1-medium
            `}
          >
            <p className="font-bold">Detalles de la oferta</p>
            {(paymentData.typeOfPayment === 'Financiado'
              ? [
                  { label: 'Precio financiado', value: rightPrice.price },
                  {
                    label: `${WarrantyTranslationEnum[paymentData.warrantyChoice.type]} x ${paymentData.warrantyChoice.duration} años`,
                    value: formatPrice(
                      warrantyPriceByWarrantyType({
                        ...paymentData.warrantyChoice,
                        hasPackOptima: paymentData.hasPackOptima,
                      }),
                    ),
                  },
                  ...machanicAndBatteryWarrantyDetails,
                  {
                    label: 'Entrada inicial',
                    value: formatPrice(paymentData.initialEntry),
                  },
                  {
                    label: 'Plazo (meses)',
                    value: paymentData.activeNumber,
                  },
                  {
                    label: 'TIN',
                    value: `${fianceAmounts.applicableTIN}%`,
                  },
                ]
              : [
                  { label: 'Precio al contado', value: rightPrice.price },
                  {
                    label: `${WarrantyTranslationEnum[paymentData.warrantyChoice.type]} x ${paymentData.warrantyChoice.duration} años`,
                    value: formatPrice(
                      warrantyPriceByWarrantyType({
                        ...paymentData.warrantyChoice,
                        hasPackOptima: paymentData.hasPackOptima,
                      }),
                    ),
                  },
                  ...machanicAndBatteryWarrantyDetails,
                ]
            ).map(({ label, value }) => {
              return (
                <div className="flex justify-between" key={label}>
                  <p>{label}</p>
                  <p>{value}</p>
                </div>
              )
            })}
          </div>
        )}
        <VehicleCertificates
          className={`xl:pl-6`}
          isFinanced={paymentData?.typeOfPayment === 'Financiado'}
        />
      </div>
    </div>
  )
}
